var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/* eslint-disable import/no-extraneous-dependencies, @typescript-eslint/naming-convention */
import { useEffect, useRef, useMemo, useCallback, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchEventSource, EventStreamContentType } from '@microsoft/fetch-event-source';
// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
/**
 * HTTP Response Error for SSE connection failures
 * Used to distinguish client errors (4xx) from server errors (5xx) for retry logic
 */
export class HttpResponseError extends Error {
    constructor(status, statusText) {
        super(`SSE connection failed: ${status} ${statusText}`);
        this.name = 'HttpResponseError';
        this.status = status;
        this.statusText = statusText;
    }
}
// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const DEFAULT_HEARTBEAT_TIMEOUT_MS = 45000;
/**
 * Maximum number of SSE reconnection retries before giving up.
 * Bumped from 6 → 20 because the prior cap exhausted in ~63 s (1+2+4+8+16+32 backoff)
 * and stranded users on the manual "Try reconnecting" button after a brief network blip.
 * With the 30 s backoff cap below, 20 retries spans ~8–10 minutes of patient retrying.
 */
export const SSE_MAX_RETRIES = 20;
/** Initial backoff delay in ms for SSE reconnection (multiplier: 2×, capped at SSE_MAX_BACKOFF_MS) */
export const SSE_INITIAL_BACKOFF_MS = 1000;
/** Cap on the exponential backoff so later retries don't wait minutes between attempts. */
export const SSE_MAX_BACKOFF_MS = 30000;
/**
 * Maximum random jitter added to each backoff to break up synchronized retry
 * storms. When N tabs all get hit by an upstream 502 at once, unjittered
 * retries land on the same timer and re-collide, multiplying load on the
 * recovering service. Jitter desynchronizes them.
 */
export const SSE_BACKOFF_JITTER_MAX_MS = 1000;
function computeBackoffMs(attempt) {
    const delay = SSE_INITIAL_BACKOFF_MS * 2 ** Math.max(0, attempt - 1);
    const capped = Math.min(delay, SSE_MAX_BACKOFF_MS);
    const jitter = Math.random() * SSE_BACKOFF_JITTER_MAX_MS;
    return capped + jitter;
}
// ---------------------------------------------------------------------------
// Helper: get CSRF token from meta tag
// ---------------------------------------------------------------------------
function getCsrfToken() {
    var _a;
    return ((_a = document.querySelector('meta[name="csrf-token"]')) === null || _a === void 0 ? void 0 : _a.getAttribute('content')) || '';
}
// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------
export function useQuerySubscription({ queryKey, url, enabled = true, method = 'GET', body, headers = {}, eventHandlers, initialData, onOpen, onError, onClose, onAuthError, heartbeatTimeoutMs = DEFAULT_HEARTBEAT_TIMEOUT_MS }) {
    const queryClient = useQueryClient();
    const abortRef = useRef(null);
    const isConnectedRef = useRef(false);
    const [isConnected, setIsConnected] = useState(false);
    const [reconnectCount, setReconnectCount] = useState(0);
    const [connectionState, setConnectionState] = useState('connecting');
    const [reconnectAttempts, setReconnectAttempts] = useState(0);
    const heartbeatTimerRef = useRef(null);
    const lastEventIdRef = useRef('');
    const streamIdentityRef = useRef(null);
    // Serialize body and headers to stable strings for dependency array
    const serializedBody = useMemo(() => (body ? JSON.stringify(body) : ''), [body]);
    const serializedHeaders = useMemo(() => JSON.stringify(headers), [headers]);
    // Stable ref for lifecycle callbacks to avoid re-subscriptions
    const callbacksRef = useRef({ onOpen, onError, onClose, onAuthError });
    useEffect(() => {
        callbacksRef.current = { onOpen, onError, onClose, onAuthError };
    }, [onOpen, onError, onClose, onAuthError]);
    // Seed cache with initial data on first render
    const { data, error, isLoading } = useQuery({
        queryKey,
        queryFn: () => {
            const cached = queryClient.getQueryData(queryKey);
            if (cached !== undefined)
                return cached;
            return undefined;
        },
        enabled,
        initialData,
        staleTime: Infinity,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false
    });
    // Stable reference to event handlers to avoid re-subscribing on every render
    const handlersRef = useRef(eventHandlers);
    handlersRef.current = eventHandlers;
    const resetHeartbeat = useCallback(() => {
        if (heartbeatTimerRef.current) {
            clearTimeout(heartbeatTimerRef.current);
        }
        heartbeatTimerRef.current = setTimeout(() => {
            // Heartbeat timeout — abort current connection and trigger reconnect
            if (abortRef.current) {
                abortRef.current.abort();
                setReconnectCount((prev) => prev + 1);
            }
        }, heartbeatTimeoutMs);
    }, [heartbeatTimeoutMs]);
    useEffect(() => {
        // Identity is based on logical endpoint only — auth headers are intentionally excluded
        // so that a token refresh (which changes serializedHeaders) does not reset Last-Event-ID
        // and cause the server to replay already-received events.
        const streamIdentity = `${method}:${url}:${serializedBody}:${JSON.stringify(queryKey)}`;
        if (streamIdentityRef.current !== streamIdentity) {
            // New stream target (e.g. conversation switch): Last-Event-ID from a prior
            // stream must not be sent to this one.
            lastEventIdRef.current = '';
            streamIdentityRef.current = streamIdentity;
        }
    }, [method, queryKey, serializedBody, url]);
    useEffect(() => {
        if (!enabled || !url)
            return undefined;
        const ctrl = new AbortController();
        abortRef.current = ctrl;
        // Local counter for transient retries within this effect session, used to
        // compute the capped exponential backoff returned from onerror.
        let inEffectAttempts = 0;
        const requestHeaders = Object.assign({ Accept: EventStreamContentType, 'X-CSRF-Token': getCsrfToken() }, headers);
        // Add Content-Type for JSON bodies
        if (body) {
            requestHeaders['Content-Type'] = 'application/json';
        }
        if (lastEventIdRef.current) {
            requestHeaders['Last-Event-ID'] = lastEventIdRef.current;
        }
        void fetchEventSource(url, {
            method,
            headers: requestHeaders,
            body: body ? JSON.stringify(body) : undefined,
            signal: ctrl.signal,
            openWhenHidden: true,
            // eslint-disable-next-line @typescript-eslint/require-await
            onopen: (response) => __awaiter(this, void 0, void 0, function* () {
                var _a, _b, _c;
                if (response.ok && ((_a = response.headers.get('content-type')) === null || _a === void 0 ? void 0 : _a.includes(EventStreamContentType))) {
                    isConnectedRef.current = true;
                    setIsConnected(true);
                    setConnectionState('connected');
                    inEffectAttempts = 0;
                    setReconnectAttempts(0);
                    resetHeartbeat();
                    (_c = (_b = callbacksRef.current).onOpen) === null || _c === void 0 ? void 0 : _c.call(_b);
                    return;
                }
                throw new HttpResponseError(response.status, response.statusText);
            }),
            onmessage: (event) => {
                resetHeartbeat();
                if (event.id) {
                    lastEventIdRef.current = event.id;
                }
                const handler = handlersRef.current[event.event];
                if (!handler)
                    return;
                try {
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                    const eventData = event.data ? JSON.parse(event.data) : {};
                    const currentData = queryClient.getQueryData(queryKey);
                    const nextData = handler(eventData, currentData);
                    queryClient.setQueryData(queryKey, nextData);
                }
                catch (parseError) {
                    console.error('[useQuerySubscription] Failed to process event:', event.event, parseError);
                }
            },
            onclose: () => {
                var _a, _b;
                isConnectedRef.current = false;
                setIsConnected(false);
                if (heartbeatTimerRef.current) {
                    clearTimeout(heartbeatTimerRef.current);
                }
                (_b = (_a = callbacksRef.current).onClose) === null || _b === void 0 ? void 0 : _b.call(_a);
            },
            onerror: (err) => {
                var _a, _b, _c, _d, _e, _f;
                isConnectedRef.current = false;
                setIsConnected(false);
                if (heartbeatTimerRef.current) {
                    clearTimeout(heartbeatTimerRef.current);
                }
                if (err instanceof Error && err.name === 'AbortError') {
                    // Re-throw AbortError to stop fetchEventSource from retrying with aborted signal
                    throw err;
                }
                if (err instanceof HttpResponseError && err.status === 401 && callbacksRef.current.onAuthError) {
                    // 401 Unauthorized with an auth-error handler registered.
                    // Delegate to the handler; it returns true if it is initiating a
                    // token refresh + reconnect (suppresses the terminal-error transition)
                    // or false if the refresh itself failed (fall through to error state).
                    const handled = callbacksRef.current.onAuthError(err);
                    if (!handled) {
                        setConnectionState('error');
                        (_b = (_a = callbacksRef.current).onError) === null || _b === void 0 ? void 0 : _b.call(_a, err);
                    }
                    else {
                        // Surface a renderable "recovering" state while the token refresh
                        // is in flight. Without this, connectionState never leaves
                        // 'connected' during auth recovery, so the ReconnectionBanner's
                        // `connectionState === 'reconnecting'` gate never fires and the
                        // "Refreshing your session…" copy (wasAuthRefresh) never renders.
                        setConnectionState('reconnecting');
                        setReconnectAttempts((prev) => Math.max(prev, 1));
                    }
                    throw err;
                }
                if (err instanceof HttpResponseError && err.status >= 400 && err.status < 500) {
                    // Re-throw 4xx client errors to stop retries (permanent errors)
                    setConnectionState('error');
                    (_d = (_c = callbacksRef.current).onError) === null || _d === void 0 ? void 0 : _d.call(_c, err);
                    throw err;
                }
                // For 5xx server errors and other transient errors, track reconnect attempts
                let shouldStopRetrying = false;
                inEffectAttempts += 1;
                const nextAttempt = inEffectAttempts;
                setReconnectAttempts((prev) => {
                    const next = prev + 1;
                    if (next >= SSE_MAX_RETRIES) {
                        setConnectionState('error');
                        shouldStopRetrying = true;
                    }
                    else {
                        setConnectionState('reconnecting');
                    }
                    return next;
                });
                (_f = (_e = callbacksRef.current).onError) === null || _f === void 0 ? void 0 : _f.call(_e, err instanceof Error ? err : new Error(String(err)));
                // If max retries reached, throw error to stop fetchEventSource from retrying
                if (shouldStopRetrying) {
                    const maxRetriesError = new Error(`SSE connection failed after ${SSE_MAX_RETRIES} retry attempts`);
                    maxRetriesError.name = 'MaxRetriesExceeded';
                    throw maxRetriesError;
                }
                // Return capped backoff so later attempts don't wait minutes between tries
                return computeBackoffMs(nextAttempt);
            }
        }).catch((err) => {
            var _a, _b;
            // Only suppress expected terminal errors that were already handled in onerror
            const isExpectedError = (err instanceof Error && err.name === 'AbortError') ||
                (err instanceof Error && err.name === 'MaxRetriesExceeded') ||
                (err instanceof HttpResponseError && err.status >= 400 && err.status < 500);
            if (!isExpectedError) {
                // Unexpected error - forward to onError callback for visibility
                (_b = (_a = callbacksRef.current).onError) === null || _b === void 0 ? void 0 : _b.call(_a, err instanceof Error ? err : new Error(String(err)));
            }
            // Expected errors (AbortError, MaxRetriesExceeded, 4xx) are already handled in onerror callback
        });
        // Always start a fresh attempt in 'connecting'. Only `onerror` (which also
        // increments `reconnectAttempts`) is allowed to move the state to
        // 'reconnecting'. Otherwise heartbeat-driven and manual re-connects would
        // flip the banner to "Reconnecting… Attempt 0 of 20" before any retry has
        // actually been counted — a misleading display that persists if the new
        // fetchEventSource stalls in handshake (no onopen, no onerror).
        setConnectionState('connecting');
        return () => {
            ctrl.abort();
            isConnectedRef.current = false;
            setIsConnected(false);
            if (heartbeatTimerRef.current) {
                clearTimeout(heartbeatTimerRef.current);
            }
        };
    }, [enabled, url, method, serializedBody, serializedHeaders, resetHeartbeat, queryClient, queryKey, reconnectCount]);
    const abort = useCallback(() => {
        if (abortRef.current) {
            abortRef.current.abort();
        }
    }, []);
    const reconnect = useCallback(() => {
        setReconnectAttempts(0);
        setConnectionState('connecting');
        setReconnectCount((prev) => prev + 1);
    }, []);
    return useMemo(() => ({
        data,
        isConnected,
        error: error || null,
        isLoading,
        abort,
        reconnect,
        connectionState,
        reconnectAttempts
    }), [data, isConnected, error, isLoading, abort, reconnect, connectionState, reconnectAttempts]);
}
